import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MdLocalShipping, MdOpenInNew, MdRefresh, MdReplay } from 'react-icons/md'
import AdminLayout from '@/components/layout/AdminLayout'
import { adminAPI, type AdminFulfillmentOrderResponse, type AdminFulfillmentRateRecreationResponse } from '@/lib/api/admin'

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
})

const dateFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit'
})

export default function AdminFulfillmentPage() {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<AdminFulfillmentOrderResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [actionId, setActionId] = useState<string | null>(null)
  const [ratePreview, setRatePreview] = useState<AdminFulfillmentRateRecreationResponse | null>(null)

  const stats = useMemo(() => {
    const failed = orders.filter(order => isFailedLabel(order)).length
    const missingTracking = orders.filter(order => !order.shippingTrackingNumber).length
    const inTransit = orders.filter(order => normalize(order.shippingTrackingStatus) === 'TRANSIT').length
    const delivered = orders.filter(order => normalize(order.shippingTrackingStatus) === 'DELIVERED').length
    return { failed, missingTracking, inTransit, delivered }
  }, [orders])

  const loadOrders = async () => {
    setError('')
    setLoading(true)
    try {
      setOrders(await adminAPI.getFulfillmentOrders())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fulfillment orders')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadOrders()
  }, [])

  const handleLogout = () => {
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    navigate('/admin/login')
  }

  const replaceOrder = (updated: AdminFulfillmentOrderResponse) => {
    setOrders(current => current.map(order => order.paymentId === updated.paymentId ? updated : order))
  }

  const retryLabel = async (paymentId: string) => {
    setActionId(paymentId)
    setError('')
    setSuccess('')
    try {
      const updated = await adminAPI.retryFulfillmentLabel(paymentId)
      replaceOrder(updated)
      setSuccess(updated.shippingTrackingNumber ? 'Label purchased and tracking saved.' : 'Label retry finished.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to retry label purchase')
    } finally {
      setActionId(null)
    }
  }

  const recreateRates = async (paymentId: string) => {
    setActionId(paymentId)
    setError('')
    setSuccess('')
    setRatePreview(null)
    try {
      const preview = await adminAPI.recreateFulfillmentRates(paymentId)
      setRatePreview(preview)
      setSuccess(preview.matchedRequestedService
        ? 'Fresh rates created. UPS Ground Saver is available.'
        : 'Fresh rates created. Original service was not available; review the selected fallback.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to recreate rates')
    } finally {
      setActionId(null)
    }
  }

  const refreshTracking = async (paymentId: string) => {
    setActionId(paymentId)
    setError('')
    setSuccess('')
    try {
      const updated = await adminAPI.refreshFulfillmentTracking(paymentId)
      replaceOrder(updated)
      setSuccess('Tracking status refreshed.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh tracking')
    } finally {
      setActionId(null)
    }
  }

  const refreshLabelRefund = async (paymentId: string) => {
    setActionId(paymentId)
    setError('')
    setSuccess('')
    try {
      const updated = await adminAPI.refreshFulfillmentLabelRefund(paymentId)
      replaceOrder(updated)
      setSuccess('Label refund status refreshed.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh label refund')
    } finally {
      setActionId(null)
    }
  }

  return (
    <AdminLayout onLogout={handleLogout}>
      <div className="min-h-screen" style={{ backgroundColor: 'var(--background-secondary)' }}>
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Fulfillment</h1>
              <p className="mt-1 text-sm text-gray-600">Paid orders, label purchases, and Shippo tracking refreshes.</p>
            </div>
            <button
              type="button"
              onClick={loadOrders}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50"
            >
              <MdRefresh className="h-5 w-5" />
              Refresh
            </button>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-4">
            <Stat label="Label failures" value={stats.failed} tone="danger" />
            <Stat label="Missing tracking" value={stats.missingTracking} tone="warning" />
            <Stat label="In transit" value={stats.inTransit} tone="info" />
            <Stat label="Delivered" value={stats.delivered} tone="success" />
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-800">
              {error}
            </div>
          )}
          {success && (
            <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-medium text-green-800">
              {success}
            </div>
          )}
          {ratePreview && (
            <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
              <div className="font-semibold">Fresh rate preview</div>
              <div className="mt-1">
                Selected {ratePreview.selectedRate.provider} {ratePreview.selectedRate.serviceLevelName} for{' '}
                {formatMoney(ratePreview.selectedRate.amount)}
                {ratePreview.selectedRate.estimatedDays ? `, estimated ${ratePreview.selectedRate.estimatedDays} days` : ''}
              </div>
              <div className="mt-1 text-xs text-blue-800">
                Rate ID {ratePreview.selectedRate.rateId} · Shipment ID {ratePreview.selectedRate.shipmentId}
              </div>
              <div className="mt-1 text-xs text-blue-800">
                From {ratePreview.originSummary} to {ratePreview.destinationSummary}
              </div>
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            {loading ? (
              <div className="px-6 py-12 text-center text-sm text-gray-600">Loading fulfillment orders...</div>
            ) : orders.length === 0 ? (
              <div className="px-6 py-12 text-center text-sm text-gray-600">No paid orders yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-3">Order</th>
                      <th className="px-4 py-3">Amount</th>
                      <th className="px-4 py-3">Label</th>
                      <th className="px-4 py-3">Tracking</th>
                      <th className="px-4 py-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {orders.map(order => (
                      <tr key={order.paymentId} className="align-top">
                        <td className="px-4 py-4">
                          <div className="font-semibold text-gray-900">{order.listingTitle}</div>
                          <div className="mt-1 text-xs text-gray-500">{order.paymentId}</div>
                          {order.listingSlug && (
                            <a
                              href={`/marketplace/${order.listingSlug}`}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:underline"
                            >
                              View listing
                              <MdOpenInNew className="h-4 w-4" />
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-semibold text-gray-900">{formatMoney(order.totalAmount)}</div>
                          <div className="mt-1 text-xs text-gray-500">Shipping {formatMoney(order.shippingAmount)}</div>
                          <div className="mt-1 text-xs text-gray-500">{formatDate(order.paidAt || order.createdAt)}</div>
                        </td>
                        <td className="px-4 py-4">
                          <StatusBadge value={order.shippingLabelStatus || 'Missing'} />
                          <div className="mt-2 text-xs text-gray-600">
                            {shippingServiceLabel(order)}
                          </div>
                          {order.shippingLabelError && (
                            <div className="mt-2 max-w-xs rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">
                              {order.shippingLabelError}
                            </div>
                          )}
                          {order.shippingLabelRefundStatus && (
                            <div className="mt-2 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-700">
                              Label refund: {order.shippingLabelRefundStatus}
                              {order.shippingLabelRefundRequestedAt ? ` (${formatDate(order.shippingLabelRefundRequestedAt)})` : ''}
                              <div className="mt-1 text-gray-500">
                                Last check {formatDate(order.shippingLabelRefundLastCheckedAt)}
                              </div>
                            </div>
                          )}
                          {order.shippingLabelRefundError && (
                            <div className="mt-2 max-w-xs rounded-md bg-red-50 px-2 py-1 text-xs text-red-700">
                              Refund error: {order.shippingLabelRefundError}
                            </div>
                          )}
                          {order.shippingLabelUrl && (
                            <a
                              href={order.shippingLabelUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:underline"
                            >
                              Label PDF
                              <MdOpenInNew className="h-4 w-4" />
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <StatusBadge value={order.shippingTrackingStatus || 'Not ready'} />
                          <div className="mt-2 font-mono text-xs text-gray-700">
                            {order.shippingTrackingNumber || 'No tracking number'}
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            Last refresh {formatDate(order.shippingTrackingLastRefreshedAt)}
                          </div>
                          {order.shippingTrackingUrl && (
                            <a
                              href={order.shippingTrackingUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:underline"
                            >
                              Carrier tracking
                              <MdOpenInNew className="h-4 w-4" />
                            </a>
                          )}
                        </td>
                        <td className="px-4 py-4">
                          <div className="flex flex-col items-stretch gap-2 sm:items-end">
                            {shouldShowRetry(order) && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => retryLabel(order.paymentId)}
                                  disabled={actionId === order.paymentId || !order.shippoRateId}
                                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-primary px-3 py-2 text-xs font-semibold text-white transition hover:bg-brand-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <MdReplay className="h-4 w-4" />
                                  Retry label
                                </button>
                                <button
                                  type="button"
                                  onClick={() => recreateRates(order.paymentId)}
                                  disabled={actionId === order.paymentId}
                                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <MdRefresh className="h-4 w-4" />
                                  Recreate rates
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => refreshTracking(order.paymentId)}
                              disabled={actionId === order.paymentId || !order.shippingTrackingNumber}
                              className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <MdLocalShipping className="h-4 w-4" />
                              Refresh tracking
                            </button>
                            {shouldShowRefundRefresh(order) && (
                              <button
                                type="button"
                                onClick={() => refreshLabelRefund(order.paymentId)}
                                disabled={actionId === order.paymentId}
                                className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <MdRefresh className="h-4 w-4" />
                                Refresh refund
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  )
}

function Stat({ label, value, tone }: { label: string; value: number; tone: 'danger' | 'warning' | 'info' | 'success' }) {
  const colors = {
    danger: 'text-red-700 bg-red-50 border-red-200',
    warning: 'text-amber-700 bg-amber-50 border-amber-200',
    info: 'text-blue-700 bg-blue-50 border-blue-200',
    success: 'text-green-700 bg-green-50 border-green-200'
  }

  return (
    <div className={`rounded-lg border px-4 py-3 ${colors[tone]}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wide">{label}</div>
    </div>
  )
}

function StatusBadge({ value }: { value: string }) {
  const normalized = normalize(value)
  const tone = normalized === 'SUCCESS' || normalized === 'DELIVERED'
    ? 'bg-green-100 text-green-800'
    : normalized === 'ERROR' || normalized === 'FAILURE'
      ? 'bg-red-100 text-red-800'
      : normalized === 'TRANSIT'
        ? 'bg-blue-100 text-blue-800'
        : 'bg-gray-100 text-gray-700'

  return (
    <span className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${tone}`}>
      {value}
    </span>
  )
}

function formatMoney(value: number | null) {
  return currencyFormatter.format(value || 0)
}

function formatDate(value: string | null) {
  if (!value) {
    return 'Never'
  }
  return dateFormatter.format(new Date(value))
}

function shippingServiceLabel(order: AdminFulfillmentOrderResponse) {
  const service = [order.shippingProvider, order.shippingService].filter(Boolean).join(' ')
  if (!service) {
    return 'No shipping service'
  }
  if (!order.shippingEstimatedDays) {
    return service
  }
  return `${service}, ${order.shippingEstimatedDays} business days`
}

function shouldShowRetry(order: AdminFulfillmentOrderResponse) {
  return normalize(order.shippingLabelStatus) !== 'SUCCESS'
}

function isFailedLabel(order: AdminFulfillmentOrderResponse) {
  const status = normalize(order.shippingLabelStatus)
  return status === 'ERROR' || status === 'FAILURE'
}

function shouldShowRefundRefresh(order: AdminFulfillmentOrderResponse) {
  if (!order.shippoLabelRefundId) {
    return false
  }
  const status = normalize(order.shippingLabelRefundStatus)
  return status !== 'SUCCESS' && status !== 'ERROR'
}

function normalize(value: string | null | undefined) {
  return (value || '').trim().toUpperCase()
}
